import { Injectable } from "@angular/core";

import { combineLatest, Observable, of } from "rxjs";
import { catchError, first, map, switchMap } from "rxjs/operators";

import { dbToInt, dbToString, SessionStorage } from "@dffedb/util";

import { BrugerRepository, Rolle } from "../bruger";
import { AktuelBrugerService } from "../bruger/aktuel-bruger.service";
import { AppserverCredentials, opretTomAppserverCredentials } from "../credentials";
import { ForbrugerTilknytning, ForbrugerTilknytningRepository } from "../forbruger-tilknytning";
import { ForsyningIndstillinger, ForsyningIndstillingerService } from "../forsyning-indstillinger";
import { BrugerHttpClient, EksternForsyningHttpClient, ParametreForAktuelleInstallation } from "../http";
import { Installation } from "./model/installation.model";

export interface InstallationAfgraensning {
    skip?: number;
    take?: number;
    huskelisteId?: number;
    soegetekst?: string;
    ejendomNr?: number;
}

const defaultInstallationAfgraensning: InstallationAfgraensning = {
    skip: 0,
    take: 10,
    huskelisteId: null,
    soegetekst: ""
};

const loginInstallationSessionKey = "login.installation.key";

@Injectable({ providedIn: "root" })
export class InstallationService {
    constructor(
        private readonly brugerHttp: BrugerHttpClient,
        private readonly eksternHttp: EksternForsyningHttpClient,
        private readonly brugerRepository: BrugerRepository,
        private readonly aktuelBrugerService: AktuelBrugerService,
        private readonly forbrugerTilknytningRepository: ForbrugerTilknytningRepository,
        private readonly forsyningIndstillingerService: ForsyningIndstillingerService,
        private readonly sessionStorage: SessionStorage
    ) {}

    public hentInstallationerForEjendom(ejendomNr: number): Observable<Installation[]> {
        return this.hentInterneInstallationer({ ejendomNr }, true).pipe(map((r) => r.items));
    }

    public hentEgneInstallationer(parametre: InstallationAfgraensning = defaultInstallationAfgraensning): Observable<Installation[]> {
        return this.hentInterneInstallationer(parametre, false).pipe(map((r) => r.items));
    }

    public hentEgneOgTilknyttedeInterneInstallationer(
        parametre: InstallationAfgraensning = defaultInstallationAfgraensning
    ): Observable<HentInstallationerResultat> {
        return this.hentInterneInstallationer(parametre, true);
    }

    public hentInstallationer(
        parametre: InstallationAfgraensning = defaultInstallationAfgraensning
    ): Observable<HentInstallationerResultat> {
        const interneInstallationer$ = this.hentEgneOgTilknyttedeInterneInstallationer(parametre);
        const eksterneInstallationer$ = this.hentEksterneInstallationer();
        return combineLatest([interneInstallationer$, eksterneInstallationer$]).pipe(
            map(([interne, eksterne]) => ({
                eof: interne.eof,
                items: [...interne.items, ...eksterne]
            }))
        );
    }

    public hentEksterneInstallationer(): Observable<Installation[]> {
        const result = this.aktuelBrugerService.select().pipe(
            first(),
            switchMap((bruger) =>
                bruger?.id
                    ? this.forbrugerTilknytningRepository.hentTilknytninger(bruger.id, this.brugerHttp)
                    : of(new Array<ForbrugerTilknytning>())
            ),
            map((tilknytninger) => tilknytninger.filter((t) => t.erEksternTilknytning)),
            switchMap((tilknytninger) => {
                const empty = new Array<Installation[]>();
                const installationer = tilknytninger.map((t) => this.hentEksterneInstallationerForTilknytning(t));
                return installationer.length ? combineLatest(installationer) : of(empty);
            }),
            map((installationer) => Array<Installation>().concat(...installationer))
        );
        return result;
    }

    private hentEksterneInstallationerForTilknytning(tilknytning: ForbrugerTilknytning): Observable<Installation[]> {
        // Uden cryptId kan vi ikke kalde ekstern API – vis én række som tekst så tilknytningen stadig ses.
        if (!tilknytning?.credentials?.cryptId) {
            return of([this.opretIkkeTilgaengeligInstallation(tilknytning)]);
        }

        return this.eksternHttp.authenticate(tilknytning.credentials).pipe(
            switchMap((http) =>
                this.brugerRepository.hentBruger(http).pipe(
                    map((bruger) => ({
                        /* eslint-disable @typescript-eslint/naming-convention */
                        Soegetekst: "",
                        Skip: 0,
                        Take: 9999,
                        EBrugerId: bruger.id,
                        MedtagTilknyttede: false
                        /* eslint-enable @typescript-eslint/naming-convention */
                    })),
                    switchMap((params) => http.post("api/FindInstallationer", params, ParametreForAktuelleInstallation.false)),
                    map((installationer: any) =>
                        installationer.Installationer.map((i: any) =>
                            this.mapInstallation(i, tilknytning.forsyningIndstillinger, tilknytning.credentials)
                        )
                    )
                )
            ),
            catchError(() => {
                // Ekstern login eller API fejler – vis stadig at tilknytningen findes, uden at den kan vælges.
                return of([this.opretIkkeTilgaengeligInstallation(tilknytning)]);
            })
        );
    }

    private opretIkkeTilgaengeligInstallation(tilknytning: ForbrugerTilknytning): Installation {
        const { adresse, postNrBy } = this.udledAdresseOgPostNrBy(tilknytning);
        const navn = (tilknytning.forbrugerNavn || "").trim();
        const maalerNr = (tilknytning.tilknytningMaalerNr || "").trim();
        const fallbackAdresse = tilknytning?.ejendomNr ? `Forbruger ${tilknytning.ejendomNr}` : "";
        return {
            adresse: adresse || navn || fallbackAdresse || "Ekstern tilknytning",
            postNrBy,
            aktivNr: 0,
            ejendomNr: tilknytning.ejendomNr,
            installationNr: 0,
            maalerNr,
            maalerTypeTekst: "",
            forsyningIndstillinger: tilknytning.forsyningIndstillinger,
            credentials: tilknytning.credentials?.cryptId ? tilknytning.credentials : opretTomAppserverCredentials(),
            eksternEBrugerNavn: tilknytning.eksternEBrugerNavn || "",
            erIkkeTilgaengelig: true
        };
    }

    /** Matcher visning af normale installation-rækker: gade, post+by, forbr. nr. og måler fra tilknytningsdata. */
    private udledAdresseOgPostNrBy(t: ForbrugerTilknytning): { adresse: string; postNrBy: string } {
        const fraApi = [t.tilknytningPostNr, t.tilknytningBy]
            .filter((x) => x != null && String(x).trim() !== "")
            .map((x) => String(x).trim())
            .join(" ")
            .trim();
        if (fraApi) {
            const gadeLinje = (t.forbrugerAdresse || "")
                .split(/\r?\n/)
                .map((l) => l.trim())
                .filter(Boolean)[0];
            const adresse = (gadeLinje || t.forbrugerNavn || "").trim();
            return { adresse, postNrBy: fraApi };
        }

        const raw = (t.forbrugerAdresse || "").trim();
        if (!raw) {
            return { adresse: "", postNrBy: "" };
        }
        const linjer = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        if (linjer.length >= 2) {
            return { adresse: linjer[0], postNrBy: linjer.slice(1).join(" ") };
        }
        const enLinje = linjer[0];
        const postnrByMatch = enLinje.match(/^(.+?)\s+(\d{4})\s+(.+)$/);
        if (postnrByMatch) {
            return { adresse: postnrByMatch[1].trim(), postNrBy: `${postnrByMatch[2]} ${postnrByMatch[3].trim()}` };
        }
        return { adresse: enLinje, postNrBy: "" };
    }

    private hentInterneInstallationer(
        parametre: InstallationAfgraensning,
        medtagTilknyttede: boolean
    ): Observable<HentInstallationerResultat> {
        const credentials = this.brugerHttp.credentials;
        const forsyningIndstillinger$ = this.forsyningIndstillingerService.hentForsyningIndstillinger(credentials.forsyningId);
        const bruger$ = this.aktuelBrugerService.select();
        return combineLatest([forsyningIndstillinger$, bruger$]).pipe(
            switchMap(([forsyningIndstillinger, bruger]) => {
                if (!bruger) {
                    return of({
                        eof: true,
                        items: []
                    });
                }
                const erForbruger = Rolle.erForbruger(bruger.rolle);
                // Forbruger: Hent alle installationer da vi ikke benytter paging og kan have eksterne tilknytninger.
                // Medarbejdere: Hent 10 (default) ad gangen.
                const take = erForbruger ? 9999 : parametre.take || defaultInstallationAfgraensning.take;
                const params = {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    Soegetekst: parametre.soegetekst || defaultInstallationAfgraensning.soegetekst,
                    Skip: parametre.skip || defaultInstallationAfgraensning.skip,
                    Take: take + 1, // Vi henter en ekstra installation for at kunne bestemme 'eof'.
                    EBrugerId: bruger.id,
                    Huskeliste: bruger.huskelisteId || parametre.huskelisteId,
                    MedtagTilknyttede: medtagTilknyttede,
                    EjendomNr: parametre.ejendomNr
                    /* eslint-enable @typescript-eslint/naming-convention */
                };
                return this.brugerHttp.post("api/FindInstallationer", params, ParametreForAktuelleInstallation.false).pipe(
                    map((result) => result.Installationer as any[]),
                    map((installationer) => installationer.map((i) => this.mapInstallation(i, forsyningIndstillinger, credentials))),
                    map((installationer) => ({
                        eof: installationer.length <= take,
                        items: installationer.splice(0, take) // Eventuel ekstra installation til eof-beregning fjernes den fra resultatet
                    }))
                );
            }),
            first()
        );
    }

    private mapInstallation(data: any, forsyningIndstillinger: ForsyningIndstillinger, credentials: AppserverCredentials): Installation {
        const ejendomNr = dbToInt(data.EjendomNr);
        const aktivNr = dbToInt(data.AktivNr);
        const installationNr = dbToInt(data.InstallationNr);
        return {
            adresse: dbToString(data.Adresse),
            postNrBy: dbToString(data.PostNr) + " " + dbToString(data.By),
            aktivNr,
            ejendomNr,
            installationNr,
            maalerNr: dbToString(data["MålerNr"]),
            maalerTypeTekst: dbToString(data["Målertype"]),
            forsyningIndstillinger,
            credentials,
            erLoginInstallation: this.erLoginInstallation(credentials?.forsyningId, ejendomNr, aktivNr, installationNr)
        };
    }

    private erLoginInstallation(forsyningId: string, ejendomNr: number, aktivNr: number, installationNr: number): boolean {
        try {
            const raw = this.sessionStorage.getItem(loginInstallationSessionKey);
            const login = raw ? JSON.parse(raw) : null;
            if (!login) return false;
            return (
                (login.forsyningId || "") === (forsyningId || "") &&
                Number(login.ejendomNr || 0) === Number(ejendomNr || 0) &&
                Number(login.aktivNr || 0) === Number(aktivNr || 0) &&
                Number(login.installationNr || 0) === Number(installationNr || 0)
            );
        } catch {
            return false;
        }
    }
}

export interface HentInstallationerResultat {
    items: Installation[];
    eof: boolean;
}
